import {
  Box,
  Card,
  Flex,
  Form,
  Icon,
  Text,
  Button,
  Heading,
  CardBody,
  Textarea,
  IconButton,
  FormControl,
  FormErrorMessage,
  Stack,
  StackDivider,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { selectFullName } from '../../../../store/selectors/customerProfile'
import { useEffect, useState } from 'react'
import * as Sentry from '@sentry/browser'
import { useIntl, defineMessages } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import useViewport from '../../../../hooks/useViewport'
import { Listing } from '../../../../api/gqlaxy/gql/generated/graphql'
import { flashNotification } from '../../../../actions/notification'
import { HOME_SEARCH_LISTING_DETAILS_HBN_MESSAGE } from '../../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import { MessageTopicKey } from '../../../../api/gqlaxy/gql/generated/graphql'
import { useSendListingMessage } from '../../../../hooks/listings/useSendListingMessage'
import { hasCustomerToken } from '../../../../auth'
import { ClientTeamMembership } from '../../../../types/clientTeamMembership'
import { PreferredAgentCard } from './PreferredAgentCard/PreferredAgentCard'
import { verifyTeamMembership } from '../../../../store/slices/homebotNetwork/clientTeamMemberships'
import { AppDispatch } from '../../../../store'
import { AddPreferredAgentModal } from '../../../shared/TeamPanel/AddPreferredAgent/AddPreferredAgentModal'
import { ConfirmRemovePreferredPartnerModal } from '../../../shared/TeamPanel/RemovePreferredAgent/ConfirmRemovePreferredPartnerModal'
import { useVerificationFlowEnabled } from '../../../../hooks/useVerificationFlowEnabled'
import { useSafeVerificationFlow } from '../../../shared/Questions/hooks/useSafeVerificationFlow'
import { CUSTOMER_MESSAGES } from '../../../../constants/customerMessages'

export const MSG = defineMessages({
  head: {
    id: 'ListingDetails.UnverifiedAgentCard.head',
    defaultMessage: 'Ask a question'
  },
  headHidden: {
    id: 'ListingDetails.UnverifiedAgentCard.headHidden',
    defaultMessage: 'Have a question'
  },
  submit: {
    id: 'ListingDetails.UnverifiedAgentCard.submit',
    defaultMessage: 'Yes, send message'
  },
  defaultQuestion: {
    id: 'ListingDetails.UnverifiedAgentCard.defaultQuestion',
    defaultMessage: 'I would like to learn more about this listing.'
  },
  addedByHomebot: {
    id: 'ListingDetails.UnverifiedAgentCard.addedByHomebot',
    defaultMessage: 'Homebot found your agent from {streetAddress}'
  },
  addedByCustomer: {
    id: 'ListingDetails.UnverifiedAgentCard.addedByCustomer',
    defaultMessage: '{customerName} added a preferred agent'
  },
  addToConversationSubText: {
    id: 'ListingDetails.UnverifiedAgentCard.addToConversationSubText',
    defaultMessage:
      'Keep them on your team to help with real estate questions?'
  },
  disclaimer: {
    id: 'ListingDetails.UnverifiedAgentCard.disclaimer',
    defaultMessage:
      "By clicking 'Send Message' you authorize HB Real Estate Services, Inc. and Homebot, Inc. to share your contact information (name and email address) and any other information you include in your question with the listing agent identified above. You agree that the listing agent may may contact you about your inquiry."
  },
  fail: {
    id: 'ListingDetails.UnverifiedAgentCard.fail',
    defaultMessage: 'Message failed to send'
  },
  required: {
    id: 'ListingDetails.UnverifiedAgentCard.required',
    defaultMessage: 'Required'
  },
  show: {
    id: 'ListingDetails.UnverifiedAgentCard.show',
    defaultMessage: 'Show agent contact card'
  },
  hide: {
    id: 'ListingDetails.UnverifiedAgentCard.hide',
    defaultMessage: 'Hide agent contact card'
  },
  sendMsgImpersonating: {
    id: 'ListingDetails.UnverifiedAgentCard.sendMsgImpersonation',
    defaultMessage: 'Sending a message on behalf of your clients is not supported at this time.'
  },
  messageSent: {
    id: 'ListingDetails.UnverifiedAgentCard.messageSent',
    defaultMessage: 'Message sent!'
  },
  removeOrReplace: {
    id: 'ListingDetails.UnverifiedAgentCard.removeOrReplace',
    defaultMessage: "Don't want to work with {agentFirstName}? You can {remove} or {replace} them."
  }
})

export interface UnverifiedAgentCardProps {
  listing?: Listing
  clientTeamMembership: ClientTeamMembership
  clientId: string
  streetAddress: string
}

export interface MessageFormValues {
  question: string
}

export const UnverifiedAgentCard = ({
  listing,
  clientTeamMembership,
  clientId,
  streetAddress
}: UnverifiedAgentCardProps) => {
  const intl = useIntl()
  const dispatch = useDispatch<AppDispatch>()
  const customerName = useSelector(selectFullName)
  const agentFirstName = clientTeamMembership.customerProfile.firstName
  const isVerificationFlowEnabled = useVerificationFlowEnabled()
  const { startVerification } = useSafeVerificationFlow()

  const { isLargeOrAbove } = useViewport()
  const [isHidden, setIsHidden] = useState(false)

  const toast = useToast()
  const isImpersonating = hasCustomerToken()

  useEffect(() => {
    if (isLargeOrAbove) {
      setIsHidden(false)
    } else {
      setIsHidden(true)
    }
  }, [isLargeOrAbove])

  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState(false)
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false)

  const { mutateAsync: sendListingMessage, isLoading: isSendingListingMessage } = useSendListingMessage()

  const handleSubmit = async (data: MessageFormValues) => {
    if (isImpersonating) {
      return toast({
        description: intl.formatMessage(MSG.sendMsgImpersonating),
        status: 'warning',
        position: 'top'
      })
    }

    if (!listing?.id || !clientTeamMembership?.id) {
      return dispatch(flashNotification({ message: intl.formatMessage(MSG.fail), messageType: 'error' }))
    }

    try {
      await dispatch(
        verifyTeamMembership({
          clientTeamMembershipId: clientTeamMembership.id,
          clientId: clientId
        })
      ).unwrap()

      await sendListingMessage({
        input: {
          message: data.question,
          listingId: listing.id,
          topicKey: MessageTopicKey.ListingsDirectMessageToHomebotNetworkPreferredAgent,
          sendToPreferredAgent: true
        }
      })

      toast({
        title: intl.formatMessage(MSG.messageSent),
        status: 'success'
      })

      if (isVerificationFlowEnabled) {
        // Start verification flow for LO prequal after RE message success
        startVerification(
          CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.message.defaultMessage,
          CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey,
          CUSTOMER_MESSAGES.HBN_BUYER_PREQUAL_REQUEST.topicKey, // Using same topic for both verified and default
          'LoanOfficer'
        )
      }
    } catch (err) {
      Sentry.captureMessage('DM failed to send', {
        extra: {
          // @ts-ignore
          error: err.message || '',
          // @ts-ignore
          requestData: err.config?.data || {},
          // @ts-ignore
          requestErrors: Object.values(err?.response?.data?.errors || {})
        }
      })
      return dispatch(flashNotification({ message: intl.formatMessage(MSG.fail), messageType: 'error' }))
    }
  }

  return (
    <>
      {/* @ts-ignore */}
      <Card size={'lg'} backgroundColor='var(--hb-colors-primary-50) !important'>
        <CardBody p={4}>
          <Flex justify='space-between' align={isHidden ? 'center' : 'flex-start'}>
            <Flex gap={2} align='center'>
              <Box>
                <Heading
                  textAlign='left'
                  mb={isHidden ? 0 : 4}
                  size={isHidden ? '3xs' : 'xs'}
                  variant={isHidden ? 'eyebrow' : undefined}
                  fontWeight='semibold'
                  color={isHidden ? 'primary.700' : 'neutral.900'}
                  textTransform='uppercase'
                >
                  {isHidden ? intl.formatMessage(MSG.headHidden) : intl.formatMessage(MSG.head)}
                </Heading>
              </Box>
            </Flex>
            <IconButton
              size='xs'
              display={['flex', null, null, 'none']}
              onClick={() => setIsHidden(!isHidden)}
              icon={<Icon name={isHidden ? 'angle-up' : 'angle-down'} />}
              aria-label={intl.formatMessage(isHidden ? MSG.show : MSG.hide)}
              tracking={{
                guid: 'rnLPm2QrWyGYrRmqdQ8uMP',
                ui_context: HOME_SEARCH_LISTING_DETAILS_HBN_MESSAGE,
                descriptor: isHidden ? MSG.show.defaultMessage : MSG.hide.defaultMessage
              }}
            />
          </Flex>
          {!isHidden && (
            <>
              <Form
                onSubmit={handleSubmit}
                values={{
                  question: intl.formatMessage(MSG.defaultQuestion)
                }}
              >
                {({ formState: { errors, isValid } }) => (
                  <>
                    <FormControl isInvalid={!!errors?.question} mb={4}>
                      <Textarea
                        size='md'
                        name='question'
                        borderRadius={8}
                        color='neutral.900'
                        aria-describedby='agent-question-desc'
                        rules={{ required: intl.formatMessage(MSG.required) }}
                      />
                      <FormErrorMessage>{errors?.question?.message?.toString()}</FormErrorMessage>
                    </FormControl>
                    <PreferredAgentCard clientTeamMembership={clientTeamMembership} />
                    <Text size='xs' fontWeight='semibold' color='neutral.900'>
                      {clientTeamMembership.addedBy === 'homebot'
                        ? intl.formatMessage(MSG.addedByHomebot, { streetAddress: streetAddress })
                        : intl.formatMessage(MSG.addedByCustomer, { customerName })}
                    </Text>
                    <Text size='xs' color='neutral.900'>
                      {intl.formatMessage(MSG.addToConversationSubText)}
                    </Text>
                    <Button
                      my='4'
                      w='100%'
                      type='submit'
                      isDisabled={!isValid}
                      isLoading={isSendingListingMessage}
                      colorScheme='primary'
                      tracking={{
                        guid: 'jzzqKky4FRjEm3x24Mz4gA',
                        ui_context: HOME_SEARCH_LISTING_DETAILS_HBN_MESSAGE,
                        descriptor: MSG.submit.defaultMessage
                      }}
                    >
                      {intl.formatMessage(MSG.submit)}
                    </Button>
                    <Stack divider={<StackDivider />}>
                      <Text size='sm'>
                        {intl.formatMessage(MSG.removeOrReplace, {
                          agentFirstName: agentFirstName,
                          remove: (
                            <Button
                              colorScheme='primary'
                              variant='link'
                              onClick={() => setIsConfirmRemoveModalOpen(true)}
                              tracking={{
                                guid: 'vaLjWKQfrqqq6ufYcChELs',
                                ui_context: HOME_SEARCH_LISTING_DETAILS_HBN_MESSAGE,
                                descriptor: 'remove'
                              }}
                            >
                              remove
                            </Button>
                          ),
                          replace: (
                            <Button
                              colorScheme='primary'
                              variant='link'
                              onClick={() => setIsReplaceModalOpen(true)}
                              tracking={{
                                guid: 'odBthbi8AGdUyDxF8b2aGv',
                                ui_context: HOME_SEARCH_LISTING_DETAILS_HBN_MESSAGE,
                                descriptor: 'replace'
                              }}
                            >
                              replace
                            </Button>
                          )
                        })}
                      </Text>
                      <Text size='3xs' color='neutral.700'>
                        {intl.formatMessage(MSG.disclaimer)}
                      </Text>
                    </Stack>
                  </>
                )}
              </Form>
            </>
          )}
        </CardBody>
      </Card>
      <AddPreferredAgentModal
        isReplaceAction
        isOpen={isReplaceModalOpen}
        onClose={() => setIsReplaceModalOpen(false)}
        clientTeamMembership={clientTeamMembership}
        clientId={clientId}
        customerType={clientTeamMembership?.customerProfile?.type}
      />

      <ConfirmRemovePreferredPartnerModal
        isOpen={isConfirmRemoveModalOpen}
        onClose={() => setIsConfirmRemoveModalOpen(false)}
        membershipId={clientTeamMembership?.id}
        clientId={clientTeamMembership?.clientId}
        customerType={clientTeamMembership?.customerProfile?.type}
      />
    </>
  )
}
